import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useGarden } from '../context/GardenContext'
import type { GardeningGoal, RegionCode, SunExposure } from '../types'

const REGION_OPTIONS: { value: RegionCode; label: string; note: string }[] = [
  { value: 'OR', label: 'Oregon', note: 'Coast, valley, mountains, and high desert containers.' },
  { value: 'WA', label: 'Washington', note: 'Puget Sound, coast, Columbia Basin, and inland patios.' },
]

const SUN_OPTIONS: { value: SunExposure; label: string; note: string }[] = [
  { value: 'full-sun', label: 'Full sun', note: '6+ hours of direct sun.' },
  { value: 'partial-shade', label: 'Part shade', note: '3-6 hours, or filtered afternoon light.' },
  { value: 'full-shade', label: 'Shade', note: 'Under 3 hours of direct sun.' },
]

const GOAL_OPTIONS: { value: GardeningGoal; label: string }[] = [
  { value: 'pollinators', label: 'Attract pollinators' },
  { value: 'food', label: 'Grow food' },
  { value: 'low-maintenance', label: 'Keep it low maintenance' },
  { value: 'mixed', label: 'A little of everything' },
]

export default function Welcome() {
  const { state, dispatch } = useGarden()
  const navigate = useNavigate()
  const [region, setRegion] = useState<RegionCode>(state.region ?? 'OR')
  const [sunlight, setSunlight] = useState<SunExposure>(state.preferredSunlight ?? 'partial-shade')
  const [goal, setGoal] = useState<GardeningGoal>(state.goal ?? 'pollinators')

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    dispatch({ type: 'SET_MVP_CONDITIONS', region, sunlight, goal })
    navigate('/setup')
  }

  return (
    <div className="min-h-[calc(100vh-64px)] flex items-center justify-center px-4 py-10 page-enter">
      <form onSubmit={handleSubmit} className="relative z-10 w-full max-w-2xl ac-dialog p-6 sm:p-8 space-y-6">
        <div className="space-y-3">
          <p className="font-body font-extrabold text-ac-green-dark text-sm">Oregon and Washington public beta</p>
          <h1 className="font-pixel text-ac-brown text-xl leading-relaxed">TinyPlanter</h1>
          <p className="font-body text-ac-brown-light max-w-xl">
            Build a container garden plan with source-reviewed Pacific Northwest plants.
          </p>
        </div>

        <fieldset className="space-y-3">
          <legend className="font-body font-extrabold text-ac-brown text-sm">Where are you gardening?</legend>
          <div className="grid sm:grid-cols-2 gap-3">
            {REGION_OPTIONS.map(option => (
              <button
                type="button"
                key={option.value}
                onClick={() => setRegion(option.value)}
                className={`text-left rounded-xl border-2 p-4 font-body shadow-ac-sm transition-all ${
                  region === option.value
                    ? 'bg-ac-yellow border-ac-brown'
                    : 'bg-ac-cream border-ac-brown hover:bg-ac-bg'
                }`}
              >
                <span className="font-extrabold text-ac-brown">{option.label}</span>
                <span className="block text-xs text-ac-brown-light mt-1">{option.note}</span>
              </button>
            ))}
          </div>
        </fieldset>

        <fieldset className="space-y-3">
          <legend className="font-body font-extrabold text-ac-brown text-sm">How much sun does your main growing spot get?</legend>
          <div className="grid sm:grid-cols-3 gap-3">
            {SUN_OPTIONS.map(option => (
              <button
                type="button"
                key={option.value}
                onClick={() => setSunlight(option.value)}
                className={`text-left rounded-xl border-2 p-3 font-body transition-all ${
                  sunlight === option.value
                    ? 'bg-ac-yellow border-ac-brown shadow-ac-sm'
                    : 'bg-ac-cream border-ac-brown hover:bg-ac-bg'
                }`}
              >
                <span className="font-extrabold text-ac-brown text-sm">{option.label}</span>
                <span className="block text-xs text-ac-brown-light mt-1">{option.note}</span>
              </button>
            ))}
          </div>
        </fieldset>

        <fieldset className="space-y-3">
          <legend className="font-body font-extrabold text-ac-brown text-sm">What do you want to prioritize in your garden?</legend>
          <div className="flex flex-wrap gap-2">
            {GOAL_OPTIONS.map(option => (
              <button
                type="button"
                key={option.value}
                onClick={() => setGoal(option.value)}
                className={`filter-chip ${goal === option.value ? 'active' : ''}`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </fieldset>

        <button type="submit" className="ac-btn-green w-full">
          Set up my containers!
        </button>
      </form>
    </div>
  )
}
